import $ from "jquery";
import * as preact from "preact";
import * as _ from "underscore";

import * as analytics from "analytics";
import * as currentDate from "currentDate";
import * as deckSelection from "deckSelection";
import * as deviceDetect from "deviceDetect";
import * as editCardDialog from "editCardDialog";
import * as haptics from "haptics";
import * as keyboardShortcuts from "keyboardShortcuts";
import loadHandwritingFont from "loadHandwritingFont";
import * as localAPI from "localAPI";
import * as modalController from "modalController";
import * as practiceUtils from "practiceUtils";
import readlangClient from "readlangClient";
import * as languageMap from "shared/languageMap";
import * as sourceUtils from "shared/sourceUtils";
import * as spacedRepetition from "shared/spacedRepetition";
import * as utils from "shared/utils";
import * as soundEffect from "soundEffect";
import FlashcardSessionStore from "stores/FlashcardSessionStore";
import stores from "stores/stores";
import UserWordStore from "stores/UserWordStore";
import * as transliterations from "transliterations";
import * as tts from "tts";
import AccentInputToolbar from "views/AccentInputToolbar";
import SessionHeaderView from "views/SessionHeaderView";
import * as speakingModeControls from "views/speakingModeControls";
import ToggleSpeakingModeView from "views/ToggleSpeakingModeView";
import TranslatableTextView from "views/TranslatableTextView";
import * as walkthrough from "walkthrough";

const pageName = "flashcardsPage";
const ENTER_KEY = 13;
const SPACE_KEY = 32;

type Props = {
  appRouter: any;
  options?: {
    chosenSource?: string;
    chosenBookID?: string;
    userWordIDs?: string;
    language?: string;
  };
};

type FlashcardContext = {
  text: string;
  bookID?: {
    _id: string;
    title?: string;
  };
  URL?: string;
};

type PreparedContext = FlashcardContext & {
  before: string;
  match?: string;
  after: string;
  youTubeURL?: string;
};

type FeedbackResult = "correct" | "near" | "incorrect";

type CardPhase = "guessing" | "typing" | "typingResult" | "revealed" | "recall";

type DisplayRun = {
  pageToken: number;
  displayToken: number;
};

type State = {
  readyToTest: UserWordStore[];
  currentWord: UserWordStore | null;
  currentContextIndex: number | null;
  context: PreparedContext | null;
  contextLoading: boolean;
  typingMode: boolean;
  guessForeignWord: boolean;
  phase: CardPhase;
  answer: string;
  answerRows: number;
  addAlternativeWordVisible: boolean;
  feedback: string;
  feedbackResult?: FeedbackResult;
  noteVisible: boolean;
  noteRevealed: boolean;
  imageRevealed: boolean;
  wordAudioLoading: boolean;
  showSpeakingModeToggle: boolean;
  transliteration?: string;
  categoryText: string;
  category: string;
  pageVisible: boolean;
  typingModeHover: boolean;
};

const initialState: State = {
  readyToTest: [],
  currentWord: null,
  currentContextIndex: null,
  context: null,
  contextLoading: false,
  typingMode: false,
  guessForeignWord: false,
  phase: "guessing",
  answer: "",
  answerRows: 1,
  addAlternativeWordVisible: false,
  feedback: "",
  noteVisible: false,
  noteRevealed: false,
  imageRevealed: false,
  wordAudioLoading: false,
  showSpeakingModeToggle: false,
  transliteration: undefined,
  categoryText: "",
  category: "",
  pageVisible: false,
  typingModeHover: false,
};

type CardVisibility = {
  originalWordVisible: boolean;
  translationVisible: boolean;
  recallButtonsVisible: boolean;
  answerReadonly: boolean;
  submitAnswerVisible: boolean;
  editVisible: boolean;
  hintVisible: boolean;
  nextCardVisible: boolean;
  deleteVisible: boolean;
  imageVisible: boolean;
  imageHintVisible: boolean;
};

const visibilityForCard = ({
  phase,
  currentWord,
  guessForeignWord,
  answer,
  feedbackResult,
  imageRevealed,
}: {
  phase: CardPhase;
  currentWord: UserWordStore | null;
  guessForeignWord: boolean;
  answer: string;
  feedbackResult?: FeedbackResult;
  imageRevealed: boolean;
}): CardVisibility => {
  const hasImage = Boolean(currentWord?.state.value.imageURL);
  const originalWordVisible =
    currentWord !== null &&
    (!guessForeignWord ||
      phase === "revealed" ||
      phase === "recall" ||
      phase === "typingResult");
  const translationVisible =
    currentWord !== null &&
    (guessForeignWord || phase === "revealed" || phase === "recall");
  const recallButtonsVisible = phase === "recall";
  const typingResultVisible = phase === "typingResult";

  return {
    originalWordVisible,
    translationVisible,
    recallButtonsVisible,
    answerReadonly: typingResultVisible && feedbackResult !== "correct",
    submitAnswerVisible: phase === "typing" && answer.length > 0,
    editVisible: recallButtonsVisible || typingResultVisible,
    hintVisible:
      recallButtonsVisible &&
      currentWord !== null &&
      (!currentWord.state.value.recallAttempts ||
        currentWord.state.value.recallAttempts === 0) &&
      !currentWord.state.value.verified,
    nextCardVisible: typingResultVisible,
    deleteVisible: recallButtonsVisible || typingResultVisible,
    imageVisible:
      hasImage && (!guessForeignWord || imageRevealed || recallButtonsVisible),
    imageHintVisible:
      hasImage &&
      guessForeignWord &&
      !imageRevealed &&
      (phase === "guessing" || phase === "typing"),
  };
};

const modifierKey = () => (deviceDetect.info().mac ? "meta" : "ctrl");

const formattedTranslations = (word: UserWordStore | null) => {
  if (!word) {
    return "";
  }
  return [
    word.state.value.translation,
    ...(word.state.value.alternativeTranslations ?? []),
  ].join(" / ");
};

const maskedWord = (word: string) =>
  word
    .split("")
    .map(() => "_")
    .join("");

export const prepareFlashcardContext = (
  currentWord: UserWordStore,
  context: FlashcardContext
): PreparedContext | null => {
  const text = context.text;

  if (!text) {
    return null;
  }

  if (text.length >= 2000) {
    console.error("stupidly long context");
    return null;
  }

  // Ellipses are cosmetic: they mark a context which starts or ends mid
  // sentence, and must not leak into contexts saved from this text.
  let displayText = text;
  if (!/[A-Z]/.test(displayText.substring(0, 2))) {
    displayText = "..." + displayText;
  }
  if (
    !/\.|\?|!/.test(
      displayText.substring(displayText.length - 2, displayText.length)
    )
  ) {
    displayText += "...";
  }

  const match = new RegExp(
    "(.*\\W)(" + utils.regexpEscape(currentWord.state.value.word) + ")(\\W.*)",
    "i"
  ).exec(" " + displayText + " ");

  let before = displayText;
  let highlightedMatch: string | undefined;
  let after = "";

  if (match) {
    before = match[1].substring(1);
    highlightedMatch = match[2];
    after = match[3].substring(0, match[3].length - 1);
  }

  let youTubeURL;
  if (currentWord.hasField("youTubeID")) {
    youTubeURL =
      "http://www.youtube.com/v/" +
      currentWord.state.value.youTubeID +
      "?start=" +
      currentWord.state.value.audioStart +
      "&end=" +
      currentWord.state.value.audioEnd +
      "&autoplay=1&version=3";
  }

  return {
    text,
    before,
    match: highlightedMatch,
    after,
    bookID: context.bookID,
    URL: context.URL,
    youTubeURL,
  };
};

const contextSource = (context: PreparedContext) => {
  if (context.bookID) {
    return { bookID: context.bookID };
  }
  if (context.URL) {
    return { URL: context.URL };
  }
  return {};
};

const openEditCardDialog = (
  currentWord: UserWordStore,
  savedCallback: () => void
) => {
  editCardDialog.show(currentWord, {
    title: "Edit Card",
    savedCallback,
    doneCallback: () => {
      modalController.cancel();
    },
  });
};

const ensureFlashcardSession = () => {
  if (!stores.flashcardSession) {
    stores.flashcardSession = new FlashcardSessionStore(stores.practiceStore);
  }
  return stores.flashcardSession;
};

class FlashcardsPageView extends preact.Component<Props, State> {
  state = { ...initialState };

  private answerInputElement?: HTMLTextAreaElement;
  private nextCardButtonElement?: HTMLButtonElement;
  private suppressUpdates = false;
  private reportingRecallEase = false;
  private pageToken = 0;
  private displayToken = 0;
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private disposeRemoteUserWordsChange?: () => void;
  private disposeGroupsReCreated?: () => void;
  private disposeUserLanguageChange?: () => void;
  private previousBodyID?: string;

  async componentDidMount() {
    const pageToken = this.pageToken;
    this.previousBodyID = $("body").attr("id");
    $("body").attr("id", pageName);

    // Prime the browser voice cache so the per-card speaker icon and
    // hasWordCardAudio gate include device voices for free users on
    // Azure-uncatalogued languages.
    void tts.initialize(stores.user.currentLearningLanguage.value).then(() => {
      this.forceUpdate();
    });

    const options = this.props.options ?? {};
    if (
      ["new", "blitz"].includes(await practiceUtils.practiceType(stores.user))
    ) {
      if (!this.isPageTokenCurrent(pageToken)) {
        return;
      }

      let newUrl = "/practice";

      if (options.chosenBookID) {
        newUrl = `/practice/book/${options.chosenBookID}`;
      } else if (options.chosenSource) {
        newUrl = `/practice?source=${encodeURIComponent(options.chosenSource)}`;
      } else if (options.userWordIDs) {
        newUrl = `/practice/words/${options.userWordIDs}`;
      }

      this.props.appRouter.navigate(newUrl, { replace: true, trigger: true });
      return;
    }

    if (!this.isPageTokenCurrent(pageToken)) {
      return;
    }

    this.props.appRouter.navigate("/flashcards", { replace: true });

    ensureFlashcardSession();

    this.setupBodyForContextTranslation();
    this.setupStoreWatchers();
    this.registerKeyboardShortcuts();

    document.title = "Learn - Readlang";
    await this.displayNext();
  }

  componentDidUpdate(previousProps: Props, previousState: State) {
    const previousVisibility = this.cardVisibility(previousState);
    const visibility = this.cardVisibility();

    if (
      this.state.typingMode &&
      this.state.guessForeignWord &&
      this.state.currentWord &&
      (this.state.currentWord !== previousState.currentWord ||
        !previousState.typingMode)
    ) {
      const wordID = this.state.currentWord.id;
      const displayToken = this.displayToken;
      this.setTrackedTimeout(() => {
        if (
          displayToken === this.displayToken &&
          this.state.currentWord?.id === wordID &&
          this.state.typingMode &&
          this.state.guessForeignWord &&
          !this.cardVisibility().originalWordVisible &&
          !this.cardVisibility().nextCardVisible
        ) {
          this.answerInputElement?.focus();
        }
      }, 10);
    }
    if (!previousVisibility.nextCardVisible && visibility.nextCardVisible) {
      this.nextCardButtonElement?.focus();
    }
  }

  componentWillUnmount() {
    this.pageToken++;
    this.displayToken++;
    for (const timer of this.timers) {
      clearTimeout(timer);
    }
    this.timers.clear();
    $("body").removeAttr("data-clickMode");
    $("body").removeClass("glossStyle");
    $("body").removeClass("glossStyleNew");
    $("body").removeClass("basicLatinFirstLanguage");
    $("body").removeClass("normalTranslationFont");
    $("body").removeAttr("data-spansEnabled");
    this.disposeRemoteUserWordsChange?.();
    this.disposeGroupsReCreated?.();
    this.disposeUserLanguageChange?.();
    this.unregisterKeyboardShortcuts();
    if (this.previousBodyID) {
      $("body").attr("id", this.previousBodyID);
    } else {
      $("body").removeAttr("id");
    }
  }

  startDisplayRun(): DisplayRun {
    this.displayToken++;
    return {
      pageToken: this.pageToken,
      displayToken: this.displayToken,
    };
  }

  isPageTokenCurrent(pageToken: number) {
    return pageToken === this.pageToken;
  }

  isDisplayRunCurrent(run: DisplayRun) {
    return (
      run.pageToken === this.pageToken && run.displayToken === this.displayToken
    );
  }

  setTrackedTimeout(callback: () => void, delay: number) {
    const pageToken = this.pageToken;
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      if (this.isPageTokenCurrent(pageToken)) {
        callback();
      }
    }, delay);
    this.timers.add(timer);
    return timer;
  }

  cardVisibility(state: State = this.state) {
    return visibilityForCard({
      phase: state.phase,
      currentWord: state.currentWord,
      guessForeignWord: state.guessForeignWord,
      answer: state.answer,
      feedbackResult: state.feedbackResult,
      imageRevealed: state.imageRevealed,
    });
  }

  setupBodyForContextTranslation() {
    $("body").attr("data-clickMode", "translate");

    if (stores.user.state.value.readerTranslationStyle !== "replace") {
      if (stores.user.state.value.readerTranslationStyle === "glossNew") {
        $("body").addClass("glossStyleNew");
      } else {
        $("body").addClass("glossStyle");
      }
      $("body").toggleClass(
        "basicLatinFirstLanguage",
        languageMap.writtenInBasicLatin(stores.user.firstLanguage.value)
      );
      loadHandwritingFont();
      if (stores.user.state.value.readerTranslationFont === "normal") {
        $("body").addClass("normalTranslationFont");
      }
    }
  }

  setupStoreWatchers() {
    this.disposeGroupsReCreated = stores.practiceStore.on(
      "groupsReCreated",
      () => {
        if ($("body").attr("id") === pageName && !this.suppressUpdates) {
          this.displayNext();
        }
      }
    );
    this.disposeUserLanguageChange = stores.user.onChange(
      (user) => user.currentLearningLanguage,
      () => {
        const options = this.props.options ?? {};
        if (
          $("body").attr("id") === pageName &&
          (!options.language ||
            options.language !== stores.user.currentLearningLanguage.value)
        ) {
          stores.flashcardSession.reset();
          this.props.appRouter.navigate("/learn", { trigger: true });
        }
      }
    );
    this.disposeRemoteUserWordsChange = stores.remoteUserWords.onChange(() => {
      if ($("body").attr("id") !== pageName) {
        stores.flashcardSession.currentWord.value = null;
        stores.flashcardSession.reset();
      }
    });
  }

  registerKeyboardShortcuts() {
    keyboardShortcuts.registerHandler(
      pageName,
      ENTER_KEY,
      this.enterOrSpaceHandler
    );
    keyboardShortcuts.registerHandler(
      pageName,
      SPACE_KEY,
      this.enterOrSpaceHandler
    );
    keyboardShortcuts.registerHandler(pageName, "1".charCodeAt(0), () => {
      if (this.cardVisibility().recallButtonsVisible) {
        this.reportRecallEase(0);
      }
    });
    keyboardShortcuts.registerHandler(pageName, "2".charCodeAt(0), () => {
      if (this.cardVisibility().recallButtonsVisible) {
        this.reportRecallEase(2);
      }
    });
    keyboardShortcuts.registerHandler(pageName, "3".charCodeAt(0), () => {
      if (this.cardVisibility().recallButtonsVisible) {
        this.reportRecallEase(5);
      }
    });

    const clickDeleteButtonIfAppropriate = () => {
      if (this.state.currentWord != null) {
        this.deleteCurrentWord();
      }
    };
    keyboardShortcuts.registerHandler(
      pageName,
      "d".charCodeAt(0),
      clickDeleteButtonIfAppropriate
    );
    keyboardShortcuts.registerHandler(
      pageName,
      8,
      clickDeleteButtonIfAppropriate
    );
    keyboardShortcuts.registerHandler(pageName, 37, () => {
      this.cycleContext(false);
    });
    keyboardShortcuts.registerHandler(pageName, 39, () => {
      this.cycleContext(true);
    });
    keyboardShortcuts.registerHandler(
      pageName,
      "E".charCodeAt(0),
      () => {
        if ($("#editCardDialog").length > 0) {
          modalController.cancel();
          return;
        }

        if (this.state.typingMode && this.state.guessForeignWord) {
          this.judgeAnswer();
        } else {
          this.enterOrSpaceHandler();
        }
        this.openEditDialog();
      },
      true,
      true,
      modifierKey()
    );
    keyboardShortcuts.registerHandler(
      pageName,
      "P".charCodeAt(0),
      (event) => {
        if (
          !this.cardVisibility().originalWordVisible ||
          !this.state.currentWord
        ) {
          return;
        }
        event?.preventDefault();
        event?.stopPropagation();
        void tts.play({
          user: stores.user,
          feature: "speakingMode",
          language: stores.user.currentLearningLanguage.value,
          text: this.state.currentWord.state.value.word,
        });
      },
      true,
      true,
      modifierKey()
    );
  }

  unregisterKeyboardShortcuts() {
    const keyModifier = modifierKey();

    keyboardShortcuts.unregisterHandler(pageName, ENTER_KEY);
    keyboardShortcuts.unregisterHandler(pageName, SPACE_KEY);
    keyboardShortcuts.unregisterHandler(pageName, "1".charCodeAt(0));
    keyboardShortcuts.unregisterHandler(pageName, "2".charCodeAt(0));
    keyboardShortcuts.unregisterHandler(pageName, "3".charCodeAt(0));
    keyboardShortcuts.unregisterHandler(pageName, "d".charCodeAt(0));
    keyboardShortcuts.unregisterHandler(pageName, 8, true);
    keyboardShortcuts.unregisterHandler(pageName, 37, true);
    keyboardShortcuts.unregisterHandler(pageName, 39, true);
    keyboardShortcuts.unregisterHandler(
      pageName,
      "E".charCodeAt(0),
      true,
      keyModifier
    );
    keyboardShortcuts.unregisterHandler(
      pageName,
      "P".charCodeAt(0),
      true,
      keyModifier
    );
  }

  async startTest(
    number: number,
    selectedBookId?: string,
    readyToTestOverride?: UserWordStore[],
    run: DisplayRun = this.startDisplayRun()
  ) {
    let readyToTest = readyToTestOverride ?? this.state.readyToTest;
    const showSpeakingModeToggle = stores.user.speakingModeEnabled(
      stores.user.currentLearningLanguage.value
    );

    if (selectedBookId) {
      readyToTest = await stores.practiceStore.fetchBookWordsToTest([
        selectedBookId,
      ]);
      if (!this.isDisplayRunCurrent(run)) {
        return;
      }
      this.setState({ readyToTest });
      analytics.trackEventWithProperties("startFlashcards", {
        numberOfCards: Math.min(number, readyToTest.length),
        from: "specificText",
      });
    } else {
      analytics.trackEventWithProperties("startFlashcards", {
        numberOfCards: Math.min(number, readyToTest.length),
        from: "all",
      });
    }

    if (readyToTest.length === 0) {
      this.props.appRouter.navigate("/learn", { trigger: true });
      return;
    }

    stores.flashcardSession.start(
      readyToTest,
      number,
      stores.user.flashcardMode.value === "mixed"
    );

    this.setState({ showSpeakingModeToggle });
    await this.displayNext(run);
  }

  async displayNext(run: DisplayRun = this.startDisplayRun()) {
    const options = this.props.options ?? {};
    const numberOfCards = practiceUtils.cardsPerSession(stores.user);

    stores.flashcardSession.setCurrentWordFromDeck();
    const currentWord = stores.flashcardSession.currentWord.value;

    if (!currentWord || options.userWordIDs) {
      stores.practiceStore.resetGroups();

      if (options.userWordIDs) {
        await this.startSpecificWordsSession(options.userWordIDs, run);
        return;
      }

      if (options.chosenBookID) {
        await this.startTest(
          numberOfCards,
          options.chosenBookID,
          undefined,
          run
        );
        if (!this.isDisplayRunCurrent(run)) {
          return;
        }
        this.setState({ pageVisible: true });
        return;
      }

      if (options.chosenSource) {
        const readyToTest = await stores.practiceStore.fetchSourceWordsToTest(
          options.chosenSource
        );
        if (!this.isDisplayRunCurrent(run)) {
          return;
        }
        this.setState({ readyToTest });
        await this.startTest(
          Math.min(numberOfCards, readyToTest.length),
          undefined,
          readyToTest,
          run
        );
        if (!this.isDisplayRunCurrent(run)) {
          return;
        }
        this.setState({ pageVisible: true });
        return;
      }

      const groups = await stores.practiceStore.fetchGroups([
        "reviewedToday",
        "readyToTest",
      ]);
      const readyToTest = groups.readyToTest;
      if (!this.isDisplayRunCurrent(run)) {
        return;
      }
      this.setState({ readyToTest });
      await this.startTest(
        Math.min(numberOfCards, readyToTest.length),
        undefined,
        readyToTest,
        run
      );
      if (!this.isDisplayRunCurrent(run)) {
        return;
      }
      this.setState({ pageVisible: true });
      return;
    }

    await this.displayCurrentWord(currentWord, run);
  }

  async startSpecificWordsSession(userWordIDs: string, run: DisplayRun) {
    const specificWordIDs = userWordIDs.split("|");
    const specificWords: UserWordStore[] = [];
    const needToFetch: string[] = [];

    for (const wordID of specificWordIDs) {
      const word = stores.remoteUserWords.getWord(wordID);
      if (word) {
        specificWords.push(word);
      } else {
        needToFetch.push(wordID);
      }
    }

    if (await localAPI.isLocalMode()) {
      if (!this.isDisplayRunCurrent(run)) {
        return;
      }
      readlangClient.login();
      return;
    }

    try {
      if (needToFetch.length > 0) {
        await Promise.all(
          needToFetch.map(async (wordID) => {
            const word = new UserWordStore({ _id: wordID });
            await word.fetch();
            if (!word.hasField("word")) {
              throw new Error("no word exists with that id");
            }
            specificWords.push(word);
          })
        );
        if (!this.isDisplayRunCurrent(run)) {
          return;
        }
      }

      if (
        specificWords[0].state.value.language !==
        stores.user.currentLearningLanguage.value
      ) {
        await stores.user.save({
          currentLearningLanguage: specificWords[0].state.value.language,
        });
        if (!this.isDisplayRunCurrent(run)) {
          return;
        }
      }

      const groups = await stores.practiceStore.fetchGroups([
        "reviewedToday",
        "readyToTest",
      ]);
      const readyToTest = groups.readyToTest;
      if (!this.isDisplayRunCurrent(run)) {
        return;
      }

      stores.flashcardSession.setDeck(
        deckSelection.selectDeckWords({
          candidates: readyToTest,
          specificWords,
          sessionSize: 8,
        })
      );
      analytics.trackEventWithProperties("startFlashcards", {
        numberOfCards: stores.flashcardSession.currentDeckSize.value,
        from: "specificWords",
      });

      this.props.appRouter.navigate("/flashcards", { replace: true });
      delete this.props.options?.userWordIDs;
      await this.displayNext(run);
    } catch (error: any) {
      if (!this.isDisplayRunCurrent(run)) {
        return;
      }
      if (!error?.jqXHR?.alreadyNotified) {
        // Terminal failures (network loss, 5xx) were already notified by
        // readlangClient, so only log the missing-word diagnosis otherwise.
        // Either way, fall through to /learn — the flashcards page never
        // became visible, so staying here would leave a blank screen.
        console.error("couldn't find word", error);
      }
      this.props.appRouter.navigate("/learn", {
        trigger: true,
        replace: true,
      });
      delete this.props.options?.userWordIDs;
    }
  }

  async displayCurrentWord(currentWord: UserWordStore, run: DisplayRun) {
    const typingMode = stores.user.state.value.typingMode;
    const guessForeignWord =
      stores.user.flashcardMode.value !== "passive" &&
      currentWord.hasField("lastRecallEase") &&
      currentWord.state.value.lastRecallEase >= 1 &&
      !currentWord.state.value.passiveOverride;
    const phase =
      guessForeignWord && typingMode
        ? ("typing" as const)
        : ("guessing" as const);

    const category = await this.cardCategory(currentWord);
    if (!this.isDisplayRunCurrent(run)) {
      return;
    }

    this.setState({
      currentWord,
      currentContextIndex: null,
      context: null,
      contextLoading: currentWord.hasField("_id"),
      typingMode,
      guessForeignWord,
      phase,
      answer: "",
      answerRows: 1,
      addAlternativeWordVisible: false,
      feedback: this.initialFeedback(guessForeignWord, typingMode),
      feedbackResult: undefined,
      noteVisible: Boolean(currentWord.state.value.note),
      noteRevealed: false,
      imageRevealed: false,
      wordAudioLoading: false,
      transliteration: undefined,
      ...category,
      pageVisible: true,
    });

    // Passive cards show the foreign word from the start, so Auto-Pronounce
    // fires at display time — the revealWord path never runs for them.
    if (!guessForeignWord) {
      this.autoPronounce(currentWord);
    }

    if (currentWord.hasField("_id")) {
      await currentWord.fetch();
      if (!this.isDisplayRunCurrent(run)) {
        return;
      }
    }

    this.updateContexts(currentWord, null);
  }

  initialFeedback(guessForeignWord: boolean, typingMode: boolean) {
    if (guessForeignWord && typingMode) {
      return (
        "Type the " +
        languageMap.map[stores.user.currentLearningLanguage.value].english +
        " word in the box above"
      );
    }
    return "Try guessing first, then click the question mark to reveal the answer";
  }

  async cardCategory(currentWord: UserWordStore) {
    const lastSeen = currentWord.lastSeen();
    if (!lastSeen) {
      return { categoryText: "New", category: "notStarted" };
    }

    let date = await currentDate.getSnapped(stores.user.state.value.updateHour);
    date = new Date(date.getTime() + 1000);
    const days = Math.round(
      (date.getTime() - lastSeen.getTime()) / spacedRepetition.DAY
    );

    if (days < 1) {
      return { categoryText: "Last seen today", category: "justStarted" };
    }
    if (days === 1) {
      return { categoryText: "Last seen yesterday", category: "justStarted" };
    }
    if (days < 7) {
      return {
        categoryText: "Last seen " + days + " days ago",
        category: "gettingThere",
      };
    }
    return {
      categoryText: "Last seen " + days + " days ago",
      category: "mastered",
    };
  }

  currentContext(currentWord: UserWordStore, index: number | null) {
    const contexts = currentWord.state.value.contexts;
    if (!contexts || contexts.length === 0) {
      return {
        index: null,
        context: null,
      };
    }

    let contextIndex = index;
    if (contextIndex === null) {
      contextIndex = Math.floor(Math.random() * contexts.length);
    } else {
      contextIndex = contextIndex % contexts.length;
    }

    const context = contexts[contextIndex];
    if (!context) {
      return {
        index: contextIndex,
        context: null,
      };
    }

    return {
      index: contextIndex,
      context: prepareFlashcardContext(currentWord, context),
    };
  }

  updateContexts(currentWord: UserWordStore, index: number | null) {
    const contexts = currentWord.state.value.contexts ?? [];

    if (contexts.length > 1 && this.props.options?.chosenBookID) {
      const orderedContexts = [...contexts];
      const contextIndex = _.findIndex(orderedContexts, (context) => {
        return context.bookID?._id === this.props.options?.chosenBookID;
      });

      if (contextIndex > -1) {
        orderedContexts.unshift(orderedContexts.splice(contextIndex, 1)[0]);
        currentWord.patch({ contexts: orderedContexts });
      }
    }

    const preparedContext = this.currentContext(currentWord, index);
    this.setState({
      currentContextIndex: preparedContext.index,
      context: preparedContext.context,
      contextLoading: false,
    });
  }

  cycleContext = (forward: boolean) => {
    const currentWord = this.state.currentWord;
    const length = currentWord?.state.value.contexts?.length ?? 0;
    if (!currentWord || length === 0) {
      return;
    }

    const currentContextIndex = this.state.currentContextIndex ?? 0;
    const nextIndex =
      (currentContextIndex + length + (forward ? 1 : -1)) % length;
    this.updateContexts(currentWord, nextIndex);
  };

  revealWord = (playAudio = true) => {
    const currentWord = this.state.currentWord;
    if (!currentWord) {
      return;
    }

    this.setState({
      phase: "revealed",
    });
    this.fetchTransliterationIfAppropriate(
      currentWord,
      this.pageToken,
      this.displayToken
    );

    if (playAudio) {
      this.autoPronounce(currentWord);
    }
  };

  autoPronounce(currentWord: UserWordStore) {
    if (
      !stores.user.speakingModeEnabled(
        stores.user.currentLearningLanguage.value
      )
    ) {
      return;
    }
    void tts.play({
      user: stores.user,
      feature: "speakingMode",
      language: stores.user.currentLearningLanguage.value,
      text: currentWord.state.value.word,
    });
  }

  async fetchTransliterationIfAppropriate(
    word: UserWordStore,
    pageToken: number,
    displayToken: number
  ) {
    if (
      !(await transliterations.show(stores.user.currentLearningLanguage.value))
    ) {
      return;
    }
    if (
      !this.isPageTokenCurrent(pageToken) ||
      displayToken !== this.displayToken
    ) {
      return;
    }

    if (!word || !languageMap.map[word.state.value.language].transliterations) {
      return;
    }

    const transliteration = (
      await readlangClient.request(
        "GET",
        "/api/smartDictionary/transliteration",
        {
          language: word.state.value.language,
          type: languageMap.map[word.state.value.language]
            .transliterations?.[0],
          text: word.state.value.word,
        },
        undefined,
        undefined,
        { dataType: "text" }
      )
    ).data;

    if (
      !this.isPageTokenCurrent(pageToken) ||
      displayToken !== this.displayToken ||
      !this.state.currentWord ||
      word.id !== this.state.currentWord.id
    ) {
      console.log("we've moved on.");
      return;
    }

    this.setState({ transliteration });
  }

  showRecallButtons = () => {
    const currentWord = this.state.currentWord;
    if (!currentWord) {
      return;
    }

    this.setState({
      feedback: "",
      phase: "recall",
    });
    this.updateContexts(currentWord, this.state.currentContextIndex);
  };

  enterOrSpaceHandler = () => {
    const visibility = this.cardVisibility();

    if (this.state.typingMode && this.state.guessForeignWord) {
      return;
    }

    if (!visibility.translationVisible && !this.state.guessForeignWord) {
      this.revealTranslation();
    } else if (!visibility.originalWordVisible && this.state.guessForeignWord) {
      this.revealOriginalCard();
    }
  };

  revealTranslation = () => {
    const currentWord = this.state.currentWord;
    if (
      !currentWord ||
      this.state.guessForeignWord ||
      this.cardVisibility().translationVisible
    ) {
      return;
    }

    this.showRecallButtons();
    // Hidden until now so the learner recalls the meaning from the characters
    // rather than the pronunciation.
    this.fetchTransliterationIfAppropriate(
      currentWord,
      this.pageToken,
      this.displayToken
    );
  };

  revealOriginalCard = () => {
    if (
      !this.state.guessForeignWord ||
      this.cardVisibility().originalWordVisible
    ) {
      return;
    }

    this.revealWord();
    this.showRecallButtons();
  };

  showImage = (event: Event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({
      imageRevealed: true,
    });
  };

  changeTypingMode = async (event: Event) => {
    const pageToken = this.pageToken;
    const target = event.currentTarget as HTMLInputElement;
    await stores.user.save({ typingMode: target.checked });
    if (!this.isPageTokenCurrent(pageToken)) {
      return;
    }
    this.setTrackedTimeout(() => {
      if (this.showSessionCompleteIfAppropriate()) {
        return;
      }
      this.displayNext();
    }, 0);
  };

  onAnswerInput = (event: Event) => {
    const target = event.currentTarget as HTMLTextAreaElement;
    this.setState({
      answer: target.value,
      answerRows:
        target.scrollTop > 0
          ? this.state.answerRows + 1
          : this.state.answerRows,
    });
  };

  onAnswerKeyDown = (event: KeyboardEvent) => {
    if (event.which === ENTER_KEY) {
      event.stopPropagation();
      event.preventDefault();
      this.judgeAnswer();
    }
  };

  judgeAnswer = () => {
    const currentWord = this.state.currentWord;
    if (!currentWord) {
      return;
    }

    const answer = utils
      .normalizeString(this.state.answer.toLowerCase())
      .trim();
    const expected = utils
      .normalizeString(currentWord.state.value.word.toLowerCase())
      .trim();
    let editDistance = utils.levenshteinDistance(answer, expected);

    for (const alternativeWord of currentWord.state.value.alternativeWords ??
      []) {
      editDistance = Math.min(
        editDistance,
        utils.levenshteinDistance(
          answer,
          utils.normalizeString(alternativeWord.toLowerCase())
        )
      );
    }

    if (editDistance === 0) {
      this.reportRecallEaseFromTyping(5);
    } else if (editDistance / expected.length < 0.2) {
      this.reportRecallEaseFromTyping(2);
    } else {
      this.reportRecallEaseFromTyping(1);
    }

    this.setState({ phase: "typingResult" });
  };

  acceptAlternativeWord = () => {
    const pageToken = this.pageToken;
    const currentWord = this.state.currentWord;
    const answer = this.state.answer.trim();
    if (!currentWord) {
      return;
    }

    if (
      confirm(
        'Do you want to accept "' +
          answer +
          '" as a valid answer?\n\n' +
          "Please be absolutely sure that it fits correctly in the sentence."
      )
    ) {
      currentWord.save(
        {
          alternativeWords: [
            ...(currentWord.state.value.alternativeWords || []),
            answer,
          ],
        },
        {
          success: () => {
            if (!this.isPageTokenCurrent(pageToken)) {
              return;
            }
            this.judgeAnswer();
            this.setState({ addAlternativeWordVisible: false });
          },
          patch: true,
        }
      );
    }
  };

  /**
   * Grade the current word. Returns false if a rating was already in flight, in
   * which case this one was ignored - without that guard a second click grades
   * the next, never displayed, word.
   *
   * Grading must never be able to leave the card stuck: any failure is logged
   * and swallowed so the caller still advances, and suppressUpdates is cleared
   * in a finally so it can't permanently mute the groupsReCreated watcher.
   */
  async gradeCurrentWord(ease: number, difficulty?: number) {
    if (this.reportingRecallEase) {
      return false;
    }
    this.reportingRecallEase = true;
    this.suppressUpdates = true;
    const lengthBefore = stores.flashcardSession.currentTestDeck.value.length;
    try {
      await stores.flashcardSession.reportRecallEase(ease, difficulty);
    } catch (error) {
      console.error("Couldn't report recall ease:", error);
    } finally {
      this.reportingRecallEase = false;
      this.suppressUpdates = false;
    }

    const lengthAfter = stores.flashcardSession.currentTestDeck.value.length;
    if (lengthAfter < lengthBefore) {
      this.logWordPracticed();
    }
    return true;
  }

  reportRecallEase = async (ease: number) => {
    const pageToken = this.pageToken;
    soundEffect.play(ease >= 3 ? "correct" : "incorrect");
    haptics.play(ease >= 3 ? "success" : "error");

    const graded = await this.gradeCurrentWord(
      ease,
      this.state.guessForeignWord ? 1 : 0.5
    );
    if (!graded || !this.isPageTokenCurrent(pageToken)) {
      return;
    }

    if (this.showSessionCompleteIfAppropriate()) {
      return;
    }

    await this.displayNext();

    analytics.trackEventWithProperties("flashcardRecall", {
      "Flashcard Mode": stores.user.flashcardMode.value,
    });
    walkthrough.setAchievement("flashcards", true);
  };

  reportRecallEaseFromTyping = async (ease: number) => {
    const currentWord = this.state.currentWord;
    if (!currentWord) {
      return;
    }

    soundEffect.play(ease >= 3 ? "correct" : "incorrect");
    haptics.play(ease >= 3 ? "success" : "error");

    // Show the result straight away - the user moves on with the "next card"
    // button, so grading doesn't have to finish first
    this.revealWord();
    this.updateContexts(currentWord, this.state.currentContextIndex);

    const answerExists = this.state.answer.trim().length > 0;
    const feedbackResult =
      ease > 4 ? "correct" : ease > 3 ? "near" : "incorrect";

    this.setState({
      phase: "typingResult",
      feedback:
        ease > 4
          ? "Perfect!"
          : ease > 3
            ? "Very close!"
            : "Not quite, better luck next time.",
      feedbackResult,
      addAlternativeWordVisible: ease <= 4 && answerExists,
    });

    analytics.trackEventWithProperties("flashcardRecall", {
      "Flashcard Mode": stores.user.flashcardMode.value,
    });
    walkthrough.setAchievement("flashcards", true);

    await this.gradeCurrentWord(ease);
  };

  logWordPracticed() {
    const language = stores.user.currentLearningLanguage.value;
    void stores.streak.logPracticed(1, language);
    if (language) stores.user.markLanguageActive(language);
  }

  showSessionCompleteIfAppropriate() {
    if (stores.flashcardSession.inProgress()) {
      return false;
    }

    this.props.appRouter.navigate("/learn?sessionComplete=true", {
      trigger: true,
    });
    return true;
  }

  nextCard = (event: Event) => {
    this.setTrackedTimeout(() => {
      if (this.showSessionCompleteIfAppropriate()) {
        return;
      }
      this.displayNext();
    }, 10);
    event.stopPropagation();
    event.preventDefault();
  };

  deleteCurrentWord = () => {
    stores.flashcardSession.deleteCurrentWord();
    if (this.showSessionCompleteIfAppropriate()) {
      return;
    }
    this.displayNext();
  };

  exitTest = () => {
    analytics.trackEvent("quitFlashcards");
    stores.flashcardSession.reset();
    this.props.appRouter.navigate("/learn", { trigger: true });
  };

  openEditDialog = () => {
    const pageToken = this.pageToken;
    const currentWord = this.state.currentWord;
    if (!currentWord) {
      return;
    }

    openEditCardDialog(currentWord, () => {
      if (!this.isPageTokenCurrent(pageToken)) {
        return;
      }
      this.forceUpdate();
    });
  };

  playWordAudio = () => {
    const currentWord = this.state.currentWord;
    if (!currentWord) {
      return;
    }

    const pageToken = this.pageToken;
    const wordID = currentWord.id;
    this.setState({ wordAudioLoading: true });
    const clearLoading = () => {
      if (
        !this.isPageTokenCurrent(pageToken) ||
        this.state.currentWord?.id !== wordID
      ) {
        return;
      }
      this.setState({ wordAudioLoading: false });
    };
    void tts.play({
      user: stores.user,
      feature: "speakingMode",
      language: stores.user.currentLearningLanguage.value,
      text: currentWord.state.value.word,
      onStart: clearLoading,
      onEnd: clearLoading,
      onError: clearLoading,
    });
  };

  renderContext() {
    const visibility = this.cardVisibility();

    if (this.state.contextLoading) {
      return "Loading context...";
    }
    if (!this.state.context) {
      return "No context";
    }

    const currentWord = this.state.currentWord!;
    const language = currentWord.state.value.language;
    const targetLanguage = stores.user.firstLanguage.value;

    return (
      <FlashcardContextText
        context={this.state.context}
        sourceLanguage={language}
        targetLanguage={targetLanguage}
        source={contextSource(this.state.context)}
        maskHighlight={
          this.state.guessForeignWord && !visibility.originalWordVisible
        }
        testedWord={currentWord.state.value.word}
      />
    );
  }

  renderWordCardText() {
    const currentWord = this.state.currentWord;
    if (!currentWord) {
      return null;
    }

    if (!this.cardVisibility().originalWordVisible) {
      return "?";
    }

    const alternativeWords = currentWord.state.value.alternativeWords ?? [];

    return (
      <>
        {this.state.transliteration ? (
          <div class="transliteration">{this.state.transliteration}</div>
        ) : null}
        {currentWord.state.value.word}
        {alternativeWords.length > 0 ? (
          <>
            <br />
            <small>
              <small>or</small> {alternativeWords.join(", ")}
            </small>
          </>
        ) : null}
      </>
    );
  }

  render() {
    const flashcardSession = ensureFlashcardSession();
    const currentWord = this.state.currentWord;
    const hasWordCardAudio =
      tts.getAvailabilitySignal({
        user: stores.user,
        feature: "speakingMode",
        language: stores.user.currentLearningLanguage.value,
      }).value === "supported";
    const direction =
      languageMap.map[stores.user.currentLearningLanguage.value].direction ===
      "RTL"
        ? "RTL"
        : undefined;
    const link = this.state.context
      ? sourceUtils.linkForContext(this.state.context)
      : undefined;
    const visibility = this.cardVisibility();
    const multipleContexts =
      (currentWord?.state.value.contexts?.length ?? 0) > 1;
    const wordCardClickable =
      this.state.guessForeignWord &&
      !this.state.typingMode &&
      !visibility.originalWordVisible;
    const translationCardClickable =
      !this.state.guessForeignWord && !visibility.translationVisible;
    const wordCardInputVisible =
      this.state.guessForeignWord && this.state.typingMode;
    const wordCardTextVisible =
      !this.state.guessForeignWord ||
      !this.state.typingMode ||
      visibility.originalWordVisible;
    const wordCardAudioVisible =
      hasWordCardAudio &&
      visibility.originalWordVisible &&
      !wordCardClickable &&
      currentWord;

    const deckSize = flashcardSession.currentDeckSize.value;
    return (
      <div class="flashcardsPage">
        <SessionHeaderView
          onClose={this.exitTest}
          progress={
            deckSize
              ? 1 - flashcardSession.currentTestDeck.value.length / deckSize
              : 0
          }
        >
          <div id="toggleSpeakingModeButton">
            {this.state.showSpeakingModeToggle ? (
              <ToggleSpeakingModeView
                onClick={() => {
                  const on = !stores.user.speakingMode.value;
                  speakingModeControls.setSpeakingModeWithVoicePrompt(
                    on,
                    stores.user.currentLearningLanguage.value,
                    () => {
                      if (this.cardVisibility().originalWordVisible) {
                        this.playWordAudio();
                      }
                    }
                  );
                }}
                onSpeak={() => {
                  if (this.cardVisibility().originalWordVisible) {
                    this.playWordAudio();
                  }
                }}
              />
            ) : null}
          </div>
        </SessionHeaderView>
        <div
          class="flashcardsContainer"
          data-testid="flashcards-container"
          style={{ opacity: this.state.pageVisible ? 1 : 0 }}
        >
          <a
            href="#"
            id="imageHint"
            style={{ display: visibility.imageHintVisible ? "" : "none" }}
            onClick={this.showImage}
          >
            Show image hint
          </a>
          <img
            id="image"
            src={currentWord?.state.value.imageURL ?? undefined}
            style={{ display: visibility.imageVisible ? "" : "none" }}
          />

          <div
            id="contextContainer"
            class={multipleContexts ? "multipleContexts" : ""}
          >
            <div id="context" style={{ direction }}>
              {this.renderContext()}
            </div>
            <div
              id="youTubeLinkContainer"
              style={{
                display: this.state.context?.youTubeURL ? "block" : "none",
              }}
            >
              <a
                id="youTubeLink"
                target="_blank"
                href={this.state.context?.youTubeURL}
              >
                View on YouTube
              </a>
            </div>
            <div
              id="nextContext"
              title="Next Context"
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
                this.cycleContext(true);
              }}
            >
              <i class="icon-chevron-right icon-large" />
            </div>
          </div>

          <div id="contextLinkContainer">
            <a target="_blank" id="contextLink" href={link?.href}>
              {link?.title ?? "\u00a0"}
            </a>
          </div>

          <div id="wordCards">
            <div
              id="word"
              class={[
                "wordCard",
                wordCardClickable ? "clickable" : "",
                this.state.typingModeHover ? "disableHover" : "",
              ].join(" ")}
              style={{ direction, display: currentWord ? "inline-block" : "" }}
              onClick={wordCardClickable ? this.revealOriginalCard : undefined}
            >
              <label
                id="typingMode"
                class="cornerRibbon"
                style={{
                  display:
                    this.state.guessForeignWord &&
                    !visibility.originalWordVisible
                      ? ""
                      : "none",
                }}
                onClick={(event) => event.stopPropagation()}
                onMouseEnter={() => this.setState({ typingModeHover: true })}
                onMouseLeave={() => this.setState({ typingModeHover: false })}
              >
                Typing Mode:{" "}
                <input
                  type="checkbox"
                  checked={this.state.typingMode}
                  onChange={this.changeTypingMode}
                />
              </label>
              <div
                id="wordCardText"
                style={{ display: wordCardTextVisible ? "" : "none" }}
              >
                {this.renderWordCardText()}
              </div>
              <textarea
                id="wordCardInput"
                rows={this.state.answerRows}
                autocomplete="off"
                autocapitalize="off"
                autocorrect="off"
                spellcheck={false}
                value={this.state.answer}
                readOnly={visibility.answerReadonly}
                style={{ display: wordCardInputVisible ? "" : "none" }}
                ref={(element) => {
                  this.answerInputElement = element ?? undefined;
                }}
                onInput={this.onAnswerInput}
                onKeyDown={this.onAnswerKeyDown}
              />
              <div
                id="wordCardInputToolbar"
                style={{ display: wordCardInputVisible ? "" : "none" }}
              >
                <AccentInputToolbar
                  getInputElement={() => this.answerInputElement}
                  language={stores.user.currentLearningLanguage.value}
                  disabled={visibility.answerReadonly}
                />
              </div>
              {wordCardAudioVisible ? (
                <i
                  id="wordCardAudio"
                  title="Pronounce"
                  class={[
                    this.state.wordAudioLoading
                      ? "icon-spinner icon-spin"
                      : "icon-volume-down",
                    "needsclick",
                  ].join(" ")}
                  onClick={this.playWordAudio}
                />
              ) : null}
              <img
                id="feedbackTick"
                src={
                  this.state.feedbackResult === "correct"
                    ? "/content/feedback-check.svg"
                    : "/content/feedback-cross.svg"
                }
                class={
                  this.state.feedbackResult === "correct" ? "" : "negative"
                }
                style={{
                  display: this.state.feedbackResult ? "" : "none",
                  position: "absolute",
                  right: 13,
                  top: 13,
                }}
              />
              <button
                id="addAlternativeWord"
                class="prominent"
                style={{
                  display: this.state.addAlternativeWordVisible ? "" : "none",
                }}
                onClick={this.acceptAlternativeWord}
              >
                Accept this answer
              </button>
              <button
                id="submitAnswer"
                class="prominent"
                style={{
                  display: visibility.submitAnswerVisible ? "" : "none",
                }}
                onClick={this.judgeAnswer}
              >
                Check Answer
              </button>
            </div>
            <div class="wordCardSpacer" />
            <div
              id="translation"
              data-testid="flashcards-translation-card"
              class={[
                "wordCard",
                translationCardClickable ? "clickable" : "",
              ].join(" ")}
              style={{ display: currentWord ? "inline-block" : "" }}
              onClick={
                translationCardClickable ? this.revealTranslation : undefined
              }
            >
              <div class="cornerRibbon" data-category={this.state.category}>
                {this.state.categoryText}
              </div>
              <span id="translationText">
                {visibility.translationVisible || this.state.guessForeignWord
                  ? formattedTranslations(currentWord)
                  : "?"}
              </span>
              <div
                id="newCardHint"
                style={{ display: visibility.hintVisible ? "" : "none" }}
              >
                New card, verify and edit it &rarr;
              </div>
              <i
                id="editCard"
                title="Edit your card. It's very important to learn the correct definition!"
                class="icon-pencil clickable bottomRight"
                style={{ display: visibility.editVisible ? "" : "none" }}
                onClick={this.openEditDialog}
              />
            </div>
          </div>

          <h2
            id="feedback"
            style={{ display: this.state.feedback ? "" : "none" }}
          >
            {this.state.feedback}
          </h2>

          <div
            id="buttons"
            style={{ display: visibility.recallButtonsVisible ? "" : "none" }}
          >
            <h2>Did you remember?</h2>
            <button
              class="recall prominent"
              data-score="0"
              onClick={() => this.reportRecallEase(0)}
            >
              <small class="noMobile">1. &nbsp;</small>Not at all
            </button>
            &nbsp;
            <button
              class="recall prominent"
              data-score="2"
              onClick={() => this.reportRecallEase(2)}
            >
              <small class="noMobile">2. &nbsp;</small>Almost
            </button>
            &nbsp;
            <button
              class="recall prominent"
              data-testid="flashcards-recall-yes-button"
              data-score="5"
              onClick={() => this.reportRecallEase(5)}
            >
              <small class="noMobile">3. &nbsp;</small>Yes{" "}
              <img class="check" src="/content/white-check.svg" />
            </button>
            <br />
            <br />
          </div>

          <div>
            <button
              id="nextCardButton"
              data-testid="flashcards-next-card-button"
              class="recall prominent"
              style={{ display: visibility.nextCardVisible ? "" : "none" }}
              ref={(element) => {
                this.nextCardButtonElement = element ?? undefined;
              }}
              onClick={this.nextCard}
            >
              Next Card
            </button>
          </div>

          <button
            id="showNote"
            class="prominent"
            style={{
              display:
                this.state.noteVisible && !this.state.noteRevealed
                  ? ""
                  : "none",
            }}
            onClick={() => this.setState({ noteRevealed: true })}
          >
            <i class="icon-file-text" /> &nbsp; Show note
          </button>
          <button
            id="delete"
            class="prominent"
            style={{ display: visibility.deleteVisible ? "" : "none" }}
            onClick={this.deleteCurrentWord}
          >
            <i class="icon-remove" /> &nbsp; Delete this card
          </button>

          <p
            id="note"
            style={{ display: this.state.noteRevealed ? "" : "none" }}
          >
            <strong>NOTE: </strong>{" "}
            <span class="noteText preserveLineBreaks">
              {currentWord?.state.value.note ?? ""}
            </span>
          </p>
        </div>
      </div>
    );
  }
}

type FlashcardContextTextProps = {
  context: PreparedContext;
  sourceLanguage: string;
  targetLanguage: string;
  source: object;
  maskHighlight: boolean;
  testedWord: string;
};

export const FlashcardContextText = ({
  context,
  sourceLanguage,
  targetLanguage,
  source,
  maskHighlight,
  testedWord,
}: FlashcardContextTextProps) => {
  const highlightedText = maskHighlight
    ? maskedWord(testedWord)
    : context.match;

  return (
    <>
      <TranslatableTextView
        key={"before-" + context.text}
        text={context.before}
        context={context.text}
        sourceLanguage={sourceLanguage}
        targetLanguage={targetLanguage}
        source={source}
      />
      {context.match ? (
        <strong
          class={maskHighlight ? "hidden" : ""}
          data-original={maskHighlight ? testedWord : undefined}
        >
          {highlightedText}
        </strong>
      ) : null}
      <TranslatableTextView
        key={"after-" + context.text}
        text={context.after}
        context={context.text}
        sourceLanguage={sourceLanguage}
        targetLanguage={targetLanguage}
        source={source}
      />
    </>
  );
};

export const currentTestDeck = () =>
  ensureFlashcardSession().currentTestDeck.value;

export default FlashcardsPageView;
